import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RequireOnboarding } from '@/components/RequireOnboarding';
import { PublicOnlyRoute } from '@/components/PublicOnlyRoute';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageSpinner } from '@/components/ui/PageSpinner';
import { CookieConsentBanner } from '@/components/CookieConsentBanner';
import { Toaster } from '@/components/ui/sonner';

const LandingPage = lazy(() => import('@/pages/LandingPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const ImprintPage = lazy(() => import('@/pages/ImprintPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const BecomingPage = lazy(() => import('@/pages/BecomingPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const EvidenceLogPage = lazy(() => import('@/pages/EvidenceLogPage'));
const CommitmentsPage = lazy(() => import('@/pages/CommitmentsPage'));
// Phase 13 Plan 13-05 — InsightsPage chunk-splits p5.js out of the
// marketing/auth bundles via React.lazy. p5 ships ONLY inside this chunk
// (T-13-25 — verified by `npm run build` chunk inspection).
const InsightsPage = lazy(() => import('@/pages/InsightsPage'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
// Phase 14 Plan 14-13 (SELF-01) — the reference-photo surface outside
// onboarding, and the destination of three of the four blocked-anchor actions.
const PhotosPage = lazy(() => import('@/pages/PhotosPage'));
// Phase 15 — Memory promoted out of Settings to its own route.
const MemoryPage = lazy(() => import('@/pages/MemoryPage'));
// Phase 15 — Home replaces the placeholder dashboard; the gallery finally mounts.
const HomePage = lazy(() => import('@/pages/HomePage'));
const VisionsPage = lazy(() => import('@/pages/VisionsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

export function App() {
  return (
    <>
      {/* GDPR-05: cookie consent banner renders at SPA root, always visible before consent.
          Plan 02-05 will rebuild the banner as a Sheet primitive. */}
      <CookieConsentBanner />
      {/* sonner Toaster — Plan 02-02 (Pitfall #5: replaced legacy toast context). */}
      <Toaster richColors closeButton />
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* The landing page sits OUTSIDE MarketingLayout, unlike every other
              public route. It was ported whole from the production build and
              carries its own fixed nav and its own footer; nesting it in the
              shared marketing chrome would render two navs and two footers.
              Still logged-out only — authenticated users belong in /app. */}
          <Route element={<PublicOnlyRoute />}>
            <Route path="/" element={<LandingPage />} />
          </Route>

          {/* Marketing surface — public, marketing chrome (D-35). */}
          <Route element={<MarketingLayout />}>
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/imprint" element={<ImprintPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route element={<PublicOnlyRoute />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
            </Route>
          </Route>

          {/* Authenticated app surface — gated. AppLayout owns the
              DeletionGraceBanner mount (Plan 02-04). */}
          <Route element={<ProtectedRoute />}>
            {/* RequireOnboarding redirects a not-yet-onboarded user into the
                questionnaire before any AppLayout route renders. */}
            <Route element={<RequireOnboarding />}>
              <Route path="/app" element={<AppLayout />}>
                <Route index element={<HomePage />} />
                {/* Legacy path kept so bookmarks and the Q1 redirect below do
                    not 404. It renders Home, not the retired dashboard. */}
                <Route path="dashboard" element={<HomePage />} />
                {/* Phase 15 — VisionGallery was a complete, tested component
                    imported by nothing but its own test file. */}
                <Route path="visions" element={<VisionsPage />} />
                {/* Phase 8 — the Becoming conversational loop. */}
                <Route path="becoming" element={<BecomingPage />} />
                {/* Phase 12 (ENG-02, ENG-04) — engagement surface. */}
                <Route path="evidence" element={<EvidenceLogPage />} />
                <Route path="commitments" element={<CommitmentsPage />} />
                {/* Phase 13 Plan 13-05 — Neuro-Progress Dashboard. */}
                <Route path="insights" element={<InsightsPage />} />
                {/* Phase 14 Plan 14-13 (SELF-01, SELF-03). */}
                <Route path="photos" element={<PhotosPage />} />
                {/* Phase 15 — the transparency artifact, promoted out of a
                    Settings tab into the main navigation. The Settings tab
                    remains so existing links and tests keep working. */}
                <Route path="memory" element={<MemoryPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Route>
            {/* Onboarding questionnaire (Phase 10, VIS-01) — a top-level
                sibling inside ProtectedRoute but OUTSIDE AppLayout and
                RequireOnboarding, so a not-yet-onboarded user can reach it
                without a redirect loop. OnboardingPage owns its single
                OnboardingLayout shell. */}
            <Route path="/app/onboarding" element={<OnboardingPage />} />
          </Route>

          {/* Q1: legacy paths redirect into the new /app/* surface. */}
          <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/settings" element={<Navigate to="/app/settings" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
